import { useState, useEffect, useMemo } from 'react';
import { COMPANY_TYPE_TEMPLATES, UTM_TERM_TO_COMPANY_TYPE, PERSONALIZED_TEMPLATE_STORAGE_KEY, MULTY_VALUES_COMPANY_ENTRIES_INDICATOR } from '../constants/constants';
import type { GeneratedTemplateType } from '../types/util-types';
import AnalyticsService from '../Services/AnalyticsService';
import { AnalyticsEvents } from '../Services/AnalyticsService';
import UtilsService from '../Services/UtilsService';

interface TemplateOption {
  value: string;
  label: string;
}

interface ITemplateSelectionReturn {
  companyType: string;
  selectedTemplate: string;
  companyTypeOptions: TemplateOption[];
  templateOptions: TemplateOption[];
  setCompanyType: (value: string) => void;
  setSelectedTemplate: (value: string, explicitCompanyType?: string) => void;
  storeTemplateSelection: () => void;
  prefilledCompanyType: boolean;
}

const findCompanyTypeFromUtmTerm = (): string | null => {
  const advertisingData = UtilsService.getAdvertisingData();
  if (!advertisingData.utm_term) {
    return null;
  }

  const utmTermLower = advertisingData.utm_term.toLowerCase();
  
  const validateAndTrackCompanyType = (companyTypeValue: string): string | null => {
    const isValidCompanyType = COMPANY_TYPE_TEMPLATES.some(template => template.value === companyTypeValue);
    
    if (isValidCompanyType) {
      AnalyticsService.track(AnalyticsEvents.set_prefilled_company_type_from_utm, {
        utmTerm: advertisingData.utm_term,
        companyType: companyTypeValue
      });
      return companyTypeValue;
    }
    
    return null;
  };
  
  // Direct match check
  if (UTM_TERM_TO_COMPANY_TYPE[utmTermLower]) {
    const companyTypeValue = UTM_TERM_TO_COMPANY_TYPE[utmTermLower];
    const result = validateAndTrackCompanyType(companyTypeValue);
    if (result) return result;
  }
  
  // Partial match check - check if utm term contains any key
  const utmTermKeys = Object.keys(UTM_TERM_TO_COMPANY_TYPE);
  for (const key of utmTermKeys) {
    if (utmTermLower.includes(key)) {
      const companyTypeValue = UTM_TERM_TO_COMPANY_TYPE[key];
      const result = validateAndTrackCompanyType(companyTypeValue);
      if (result) return result;
    }
  }
  
  return null;
};

export const useTemplateSelection = (templateType: GeneratedTemplateType): ITemplateSelectionReturn => {
  const companyTypeFromUtm = useMemo(() => findCompanyTypeFromUtmTerm(), []);
  
  const initialCompanyType = companyTypeFromUtm || COMPANY_TYPE_TEMPLATES[COMPANY_TYPE_TEMPLATES.length - 1].value;
  
  const [prefilledCompanyType] = useState<boolean>(!!companyTypeFromUtm);
  const [companyType, setCompanyType] = useState<string>(initialCompanyType);
  
  const initialCompanyObj = COMPANY_TYPE_TEMPLATES.find(type => type.value === initialCompanyType);
  const [selectedCompanyTypeObj, setSelectedCompanyTypeObj] = useState(initialCompanyObj);
  
  const initialTemplate = selectedCompanyTypeObj?.templateTypes?.[templateType]?.[0]?.value || '';
  const [selectedTemplate, setSelectedTemplate] = useState<string>(initialTemplate);

  useEffect(() => {
    storeTemplateSelection();
  }, []);

  const setSelectedTemplateWithAnalytics = (value: string, explicitCompanyType?: string): void => {
    setSelectedTemplate(value);
    
    AnalyticsService.trackClick(AnalyticsEvents.template_hero_dropdown_change, {
      generatedTemplateType: templateType,
      companyType: explicitCompanyType || companyType,
      templateType: value
    });
  };

  const setCompanyTypeAndUpdateTemplate = (value: string): void => {
    setCompanyType(value);
    
    const newCompanyTypeObj = COMPANY_TYPE_TEMPLATES.find(type => type.value === value);
    setSelectedCompanyTypeObj(newCompanyTypeObj);
    
    const firstTemplateValue = newCompanyTypeObj?.templateTypes?.[templateType]?.[0]?.value;
    if (firstTemplateValue) {
      setSelectedTemplateWithAnalytics(firstTemplateValue, value);
    }
  };

  const companyTypeOptions = useMemo(() => 
    COMPANY_TYPE_TEMPLATES.map(type => ({
      value: type.value,
      label: type.label
    })),
    []
  );

  const templateOptions = useMemo(() => 
    selectedCompanyTypeObj?.templateTypes?.[templateType]?.map(template => ({
      value: template.value,
      label: template.label
    })) || [],
    [selectedCompanyTypeObj, templateType]
  );

  const storeTemplateSelection = (): void => {
    let companyTypeValue = companyType;

    if (companyTypeValue.includes(MULTY_VALUES_COMPANY_ENTRIES_INDICATOR)) {
      companyTypeValue = companyTypeValue.split(MULTY_VALUES_COMPANY_ENTRIES_INDICATOR)[0];
    }
    
    sessionStorage.setItem(PERSONALIZED_TEMPLATE_STORAGE_KEY, JSON.stringify({
      companyType: companyTypeValue,
      templateType: selectedTemplate,
    }));
  };

  return {
    companyType,
    selectedTemplate,
    companyTypeOptions,
    templateOptions,
    setCompanyType: setCompanyTypeAndUpdateTemplate,
    setSelectedTemplate: setSelectedTemplateWithAnalytics,
    storeTemplateSelection,
    prefilledCompanyType
  };
}; 